import React from "react";
import { t } from "i18next";
import { colors, fonts } from "../../Styles";
import { Consumption } from "../../Services";
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLabel, VictoryTooltip } from "victory";
import _ from "lodash";

// #region | Summed data if more animals
function combineConsumptions(arrays: Consumption[][]) {
  const combinedMap: Record<number, { progressed: number; total: number }> = {};

  // Summer alle arrays
  for (const arr of arrays) {
    for (const c of arr) {
      const offset = c.milestoneDayOffset;
      if (offset === undefined) continue;

      if (!combinedMap[offset]) {
        combinedMap[offset] = { progressed: 0, total: 0 };
      }

      combinedMap[offset].progressed += c.progressed ?? 0;
      combinedMap[offset].total += c.total ?? 0;
    }
  }

  // Lav resultat-array med offsets 0-10
  const result: Consumption[] = [];
  for (let i = 0; i <= 10; i++) {
    result.push({
      milestoneDayOffset: i,
      progressed: combinedMap[i]?.progressed ?? 0,
      total: combinedMap[i]?.total ?? 0,
    });
  }

  return result;
}
// #endregion

interface Last10DaysViewer_Props {
  data?: Consumption[] | Consumption[][];
}

export default function Last10DaysViewer({ data }: Last10DaysViewer_Props) {
  // #region | Data
  const consumptionData: Consumption[] = Array.isArray(data?.[0])
    ? combineConsumptions(data as Consumption[][])
    : (data as Consumption[]) ?? [];  
  
  const today = new Date();
  const last10Days = consumptionData.filter((consumption) => consumption.milestoneDayOffset! <= 9) //.slice(-10);
  
  const consumptionDataWithDates = last10Days.map((d, index) => ({
    ...d,
    date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - ( d.milestoneDayOffset!)),
  }));
  
  const totals = last10Days.map((d) => d.total ?? 0);
  const progresseds = last10Days.map((d) => d.progressed ?? 0);

  const maxValue = Math.max(...totals, ...progresseds);

  console.log("max", maxValue)
  
  const feedTotalRange = [0, maxValue / 1000];

  const ticks = 6;
  const tickValues = _.range(ticks + 1);

  const tickValuesX = consumptionDataWithDates.map((d) => d.date);

  const tickFormatFeed = (range: number[]) => (t: number) => {
    const value = range[0] + t * ((range[1] - range[0]) / ticks);
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/0$/, "");
  };

  const normalize = (range: number[], props: string) => (datum: { [x: string]: number }) =>
    ((datum[props] ?? 0) / 1000 - range[0]) / ((range[1] - range[0]) / ticks);

  const domain = { y: [0, ticks] as [number, number] };
  // #endregion

  // #region | Tooltip
  const tooltip = <VictoryTooltip
    dy={-3}
    cornerRadius={8}
    pointerLength={6}
    pointerWidth={12}
    flyoutPadding={{ top: 5, bottom: 5, left: 10, right: 10 }}
    flyoutStyle={{
      fill: colors.greyDarkPrimary,
      stroke: 0
    }}
    style={{
      fontFamily: fonts.paragraph.fontFamily,
      fontWeight: fonts.paragraph.fontWeight,
      fontSize: fonts.fontSize5.fontSize,
      fill: colors.greyLightTertiary,
    }}
  />
  // #endregion

  // #region | Bars
  const feedTotalBars = <VictoryBar
    data={consumptionDataWithDates}
    x="date"
    y={normalize(feedTotalRange, "total")}
    style={{
      data: {
        width: 10,
        fill: colors.graphBlueTwo,
      },
    }}
    labels={({ datum }) => `${t("feed.general.expected", { defaultValue: "Expected" })}: ${(datum.total/1000).toFixed(2)} kg`}
    labelComponent={tooltip}
  />

  const feedConsumptionBars = <VictoryBar
    data={consumptionDataWithDates}
    x="date"
    y={normalize(feedTotalRange, "progressed")}
    style={{
      data: {
        width: 10,
        fill: colors.graphBlueEight,
      },
    }}
    labels={({ datum }) => `${t("feed.general.actually", { defaultValue: "Consumed" })}: ${(datum.progressed/1000).toFixed(2)} kg`}
    labelComponent={tooltip}
  />
  // #endregion

  // #region | Axes
  // Y-Axis
  const yAxis = <VictoryAxis
    dependentAxis
    tickValues={tickValues}
    tickFormat={tickFormatFeed(feedTotalRange)}
    standalone={false}
    style={{
      ticks: { stroke: colors.greyPrimary, size: 5 },
      axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
    }}
    offsetX={70} //40
    crossAxis={false}
    orientation="left"
    label={t("units.kg", {defaultValue: "KG"})}
    tickLabelComponent={ <VictoryLabel
      text={ ({ datum }) => tickFormatFeed(feedTotalRange)(datum)}
      style={{ ...fonts.paragraph, ...fonts.fontSize4, fill: colors.greyPrimary }}
    /> }
    axisLabelComponent={ <VictoryLabel
      angle={0}
      dy={-115}
      dx={4}
      style={{ ...fonts.header, ...fonts.fontSize4, fill: colors.greyPrimary }}
    /> }
    axisComponent={ <LineSegment
      y1={26}
      y2={255}
    /> }
  />

  // X-Axis
  const xAxis = <VictoryAxis
    tickFormat={(t: Date) => {
      const day = t.getDate();          
      const month = t.getMonth() + 1;   
      return `${day}/${month}`;
    }}
    standalone={false}
    style={{
      ticks: { stroke: colors.greyPrimary, size: 5 },
      axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
    }}
    
    crossAxis={false}
    label={t("units.date", {defaultValue: "Date"})}
    tickLabelComponent={ <VictoryLabel
      dy={-3}
      style={{ ...fonts.paragraph, ...fonts.fontSize4, fill: colors.greyPrimary }}
    /> }
    axisLabelComponent={ <VictoryLabel
      dy={-13}
      dx={205}
      style={{ ...fonts.header, ...fonts.fontSize4, fill: colors.greyPrimary }}
    /> }
    axisComponent={ <LineSegment x1={65} x2={450} /> } // x1=35
    tickValues={tickValuesX}
  />
  // #endregion

  return (
    <div style={{ width: "500px", height: "340px", position: "relative", display: "block" }}>
      <VictoryChart
        domain={domain}
        width={450}
        height={300}
        domainPadding={{ x: [40, 5] }} // 10, 5
        containerComponent={ <VictoryGroup offset={12} width={450} height={300} /> }
      >
        {feedTotalBars}
        {feedConsumptionBars}
        {yAxis}
        {xAxis}
      </VictoryChart>
            
      <div style={{ display: "flex", flexDirection: "row", gap: "10px", position: "absolute", bottom: "0px", transform: "translateX(-50%)", left: "50%"}}>
        <div style={{ padding: "4px 5px", ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary, backgroundColor: colors.graphBlueTwo, borderRadius: "5px", whiteSpace: "nowrap" }}>
          {t("feed.general.expected", { defaultValue: "expected consumption" })}
        </div>
        <div style={{ padding: "4px 5px", ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary, backgroundColor: colors.graphBlueEight, borderRadius: "5px", whiteSpace: "nowrap" }}>
          {t("feed.general.actually", {defaultValue: "actually consumed"})}
        </div>
      </div>
    </div>
  );
}


// const rightAxis =
  //   rightId === 1 || rightId === 2 ? (
  //     <VictoryAxis
  //       dependentAxis
  //       style={{
  //         ticks: { stroke: colors.greyPrimary, size: 5 },
  //         axis: { stroke: colors.greyPrimary, strokeWidth: 2 },
  //       }}
  //       offsetX={30}
  //       tickValues={tickValues}
  //       axisComponent={<LineSegment y1={26} y2={255} />}
  //       orientation="right"
  //       tickFormat={
  //         rightId === 2 ? tickFormatFeed(feedTotalRange) : undefined // tickFormatActivations(activationsRange)
  //       }
  //       tickLabelComponent={
  //         <VictoryLabel
  //           text={
  //             ({ datum }) =>
  //               datum === tickValues[0]
  //                 ? ""
  //                 : rightId === 2
  //                 ? tickFormatFeed(feedTotalRange)(datum)
  //                 : "" // tickFormatActivations(activationsRange)(datum)
  //           }
  //           style={{
  //             ...fonts.paragraph,
  //             ...fonts.fontSize5,
  //             fill: colors.greyPrimary,
  //           }}
  //         />
  //       }
  //     />
  //   ) : (
  //     <></>
  //   );

  // #endregion

  
  // const feedBars = (rightId === 2 || leftId === 2)
  // ? consumptionDataWithDates.map((d, i) => {
  //   const progressedY = normalize(feedTotalRange, "progressed")(d as any);
  //   const totalY = normalize(feedTotalRange, "total")(d as any);
    
  //   const barsInOrder =
  //     (d.progressed??0) <= (d.total??0)
  //       ? [
  //           <VictoryBar
  //             key={`total-${i}`}
  //             data={[d]}
  //             x="date"
  //             y={() => totalY}
  //             style={{
  //               data: { width: 20, fill: colors.greyLightPrimary },
  //             }}
  //           />,
  //           <VictoryBar
  //             key={`progressed-${i}`}
  //             data={[d]}
  //             x="date"
  //             y={() => progressedY}
  //             style={{
  //               data: { width: 20, fill: colors.graphBlueEight },
  //             }}
  //           />,
  //         ]
  //       : [
  //           <VictoryBar
  //             key={`progressed-${i}`}
  //             data={[d]}
  //             x="date"
  //             y={() => progressedY}
  //             style={{
  //               data: { width: 20, fill: colors.graphBlueEight },
  //             }}
  //           />,
  //           <VictoryBar
  //             key={`total-${i}`}
  //             data={[d]}
  //             x="date"
  //             y={() => totalY}
  //             style={{
  //               data: { width: 20, fill: colors.greyLightPrimary },
  //             }}
  //           />,
  //         ];

  //     return barsInOrder;
  //   })
// : null;
  

// -------------------- ACTIVATIONS --------------------
  // const tickFormatActivations = (range: number[]) => (t: number) =>
  //   Math.round(range[0] + t * ((range[1] - range[0]) / ticks));

  // const activationsBars =
  //   rightId === 1 || leftId === 1 ? (
  //     <VictoryScatter
  //       data={data.slice(0, 10)}
  //       size={9}
  //       x="date"
  //       y={normalize(activationsRange, "activations")}
  //       style={{
  //         data: {
  //           fill: colors.graphActivity,
  //         },
  //       }}
  //       labels={TestData.map((d) => d.activations)}
  //       labelComponent={
  //         <VictoryLabel
  //           style={{
  //             ...fonts.header,
  //             ...fonts.fontSize5,
  //             fill: colors.whitePrimary,
  //           }}
  //           dy={5}
  //         />
  //       }
  //     />
  //   ) : (
  //     <></>
//   );
  
  // #region | SelectFields
  // const selectedValue = [
  //   { label: t("locationFeederPages.sections.activations", { defaultValue: "Activations" }), id: 1 },
  //   { label: `${t("feed.general.feed", { defaultValue: "Feed" })} (${t("units.kg", { defaultValue: "KG" } )})`, id: 2 },
  // ];

  // const handleSelectChange = (side: "left" | "right") => (e: any) => {
  //   const selectedId = e?.id; // Tjek om 'e' har en id
  //   if (side === "left" && selectedId !== undefined) {
  //     setLeftId(selectedId);
  //   } else if (side === "right" && selectedId !== undefined) {
  //     setRightId(selectedId);
  //   } else if (side === "left" && selectedId === undefined) {
  //     setLeftId(undefined);
  //   } else if (side === "right" && selectedId === undefined) {
  //     setRightId(undefined);
  //   }
  // };

  // const rightSelectField = (
  //   <NewSelect
  //     isClearable
  //     valueOverrides={{
  //       defaultValue: selectedValue.find((option) => option.id === rightId),
  //     }}
  //     options={selectedValue}
  //     onChange={handleSelectChange("right")}
  //     fieldOverrides={{
  //       fieldStyle: NewFieldStyles.greyTertiary,
  //       menuFloat: MenuFloats.left,
  //       fieldWidth: "200px",
  //     }}
  //   />
  // );

  // const leftSelectField = (
  //   <NewSelect
  //     isClearable
  //     options={selectedValue}
  //     onChange={handleSelectChange("left")}
  //     fieldOverrides={{
  //       fieldStyle: NewFieldStyles.greyTertiary,
  //       fieldWidth: "200px",
  //     }}
  //     valueOverrides={{
  //       defaultValue: selectedValue.find((option) => option.id === leftId),
  //     }}
  //   />
  // );

  // #endregion